// Client-side acquisition attribution bootstrap.
// Mints anon_id, fires first-touch capture once, links to user_id on auth.
import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { captureAttribution, linkAttribution } from "@/lib/acquisition.functions";
import { useAuth } from "@/lib/auth-context";

const ANON_KEY = "xau_anon_id";
const CAPTURED_KEY = "xau_anon_captured";
const LINKED_KEY = "xau_anon_linked";

function makeAnonId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID().replace(/-/g, "");
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getAnonId(): string {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = makeAnonId();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return makeAnonId();
  }
}

export function useAttributionBootstrap() {
  const { user } = useAuth();
  const capture = useServerFn(captureAttribution);
  const link = useServerFn(linkAttribution);

  // First-touch capture: once per browser, idempotent server-side.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem(CAPTURED_KEY)) return;
    } catch {
      return;
    }
    const anon_id = getAnonId();
    const url = new URL(window.location.href);
    const params = url.searchParams;
    capture({
      data: {
        anon_id,
        first_referrer: document.referrer || null,
        first_landing_path: url.pathname + url.search,
        first_landing_url: url.href,
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
        utm_term: params.get("utm_term"),
        utm_content: params.get("utm_content"),
      },
    })
      .then((res) => {
        if (res?.ok) {
          try { localStorage.setItem(CAPTURED_KEY, "1"); } catch { /* ignore */ }
        }
      })
      .catch(() => { /* non-fatal */ });
  }, [capture]);

  // Linkage: on first authenticated render, attach user_id to anon row.
  useEffect(() => {
    if (!user) return;
    try {
      if (localStorage.getItem(LINKED_KEY) === user.id) return;
    } catch {
      return;
    }
    const anon_id = getAnonId();
    link({ data: { anon_id } })
      .then(() => {
        try { localStorage.setItem(LINKED_KEY, user.id); } catch { /* ignore */ }
      })
      .catch(() => { /* non-fatal */ });
  }, [user, link]);
}
